import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Users,
  Building2,
  TrendingUp,
  RefreshCw,
  BarChart3,
} from 'lucide-react';

import { useClients } from '../Utils/useClients';
import ClientTable from '../Components/ClientTable';
import CreateClientButton from '../Buttons/CreateClientButton';
import BulkUploadClientButton from '../Buttons/BulkUploadClientButton';
import { ClientFormModal } from '../Modals/ClientFormModal';
import { DeleteClientModal } from '../Modals/DeleteClientModal';

const ClientsSection = ({ clientId, embedded = false }) => {
  const [, setRefreshKey] = useState(0);

  // Estados para modales
  const [editingClient, setEditingClient] = useState(null);
  const [deletingClient, setDeletingClient] = useState(null);

  const {
    clients,
    isLoading,
    error,
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    startIndex,
    endIndex,
    filters,
    stats,
    goToPage,
    changeItemsPerPage,
    applyFilters,
    clearFilters,
    refresh
  } = useClients(clientId);

  const handleRefresh = () => {
    refresh();
    setRefreshKey(prev => prev + 1);
  };

  const handleFiltersChange = (newFilters) => {
    applyFilters(newFilters);
  };

  const handleClearFilters = () => {
    clearFilters();
  };

  const handleClientCreated = (newClient) => {
    handleRefresh();
  };

  const handleClientUpdated = (updatedClient) => {
    handleRefresh();
    setEditingClient(null);
  };

  const handleClientDeleted = (deletedClient) => {
    handleRefresh();
    setDeletingClient(null);
  };

  // Funciones para abrir modales
  const handleEditClient = (client) => {
    setEditingClient(client);
  };

  const handleDeleteClient = (client) => {
    setDeletingClient(client);
  };

  // Funciones para cerrar modales
  const handleCloseEditModal = () => {
    setEditingClient(null);
  };

  const handleCloseDeleteModal = () => {
    setDeletingClient(null);
  };

  return (
    <div className={`${embedded ? '' : 'min-h-screen'} bg-gradient-to-br from-orange-50/30 to-amber-50/20 dark:from-orange-900/5 dark:to-amber-900/5 ${embedded ? 'p-0' : 'p-6'} space-y-6`}>
      {/* Header Section - Solo mostrar si no está embebido */}
      {!embedded && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-700/60 rounded shadow-md p-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  Gestión de Clientes
                </h1>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Administra y organiza tu base de clientes de manera eficiente
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                disabled={isLoading}
                className="border-slate-200/60 dark:border-slate-700/60 hover:bg-orange-50 hover:border-orange-300 dark:hover:bg-orange-900/20"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              
              <CreateClientButton 
                clientId={clientId}
                onClientCreated={handleClientCreated}
                size="default"
                className="shadow-lg"
              />

              <BulkUploadClientButton 
                clientId={clientId}
                onClientsUploaded={handleRefresh}
                size="default"
                className="shadow-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards - Solo mostrar si no está embebido */}
      {!embedded && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total Clientes
                </CardTitle>
                <Users className="h-4 w-4 text-orange-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {stats.total || 0}
              </div>
              <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400 mt-1">
                <span>{stats.countries || 0} países</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Empresas
                </CardTitle>
                <Building2 className="h-4 w-4 text-blue-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {stats.companies || 0}
              </div>
              <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400 mt-1">
                <span>{((stats.companies / stats.total) * 100 || 0).toFixed(0)}% del total</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Clientes Activos
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-green-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {stats.active || 0}
              </div>
              <div className="flex items-center space-x-2 text-xs text-green-600 dark:text-green-400 mt-1">
                <TrendingUp className="h-3 w-3" />
                <span>{((stats.active / stats.total) * 100 || 0).toFixed(0)}% activos</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Personas Naturales
                </CardTitle>
                <Users className="h-4 w-4 text-purple-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {stats.individuals || 0}
              </div>
              <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400 mt-1">
                <span>{((stats.individuals / stats.total) * 100 || 0).toFixed(0)}% del total</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Table */}
      <ClientTable
        clients={clients}
        isLoading={isLoading}
        error={error}
        onEdit={handleEditClient}
        onDelete={handleDeleteClient}
        onRefresh={handleRefresh}
        // Pagination props
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        startIndex={startIndex}
        endIndex={endIndex}
        onPageChange={goToPage}
        onItemsPerPageChange={changeItemsPerPage}
        // Filter props
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {/* Footer Info - Solo mostrar si no está embebido */}
      {!embedded && !isLoading && !error && totalItems > 0 && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-700/60 rounded shadow-md p-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
            <div className="flex items-center space-x-4">
              <Badge variant="outline" className="border-orange-200 text-orange-700 bg-orange-50 dark:border-orange-700 dark:text-orange-300 dark:bg-orange-900/20">
                <BarChart3 className="h-3 w-3 mr-1" />
                {totalItems} registros totales
              </Badge>
              {currentPage > 1 && (
                <span>Página {currentPage} de {totalPages}</span>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <span>Vista:</span>
              <Badge variant="outline" className="border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-400">
                {itemsPerPage} por página
              </Badge>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición */}
      {editingClient && (
        <ClientFormModal
          isOpen={true}
          onClose={handleCloseEditModal}
          onSuccess={handleClientUpdated}
          mode="edit"
          client={editingClient}
          title={`Editar Cliente: ${editingClient.name || 'Sin nombre'}`}
          clientId={clientId}
        />
      )}

      {/* Modal de Eliminación */}
      {deletingClient && (
        <DeleteClientModal
          isOpen={true}
          onClose={handleCloseDeleteModal}
          onConfirm={handleClientDeleted}
          client={deletingClient}
          clientId={clientId}
        />
      )}
    </div>
  );
};

export default ClientsSection; 